"use client";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Grid
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import config from "../../../../config";

const ManageLocationsModal = ({
  open,
  onClose,
  currentPlan,
  currentLocations = [], // Array of { store_type, zip_code }
  onSave,
  storeId,
  subscriptionId,
}) => {
  // STEPS: 0 = Edit Details, 1 = Review Proration
  const [step, setStep] = useState(0);

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false); // For saving
  const [prorationData, setProrationData] = useState(null);
  const [fetchingProration, setFetchingProration] = useState(false);
  const [error, setError] = useState(null);

  const currentQuantity = currentLocations.length;
  const newQuantity = locations.length;
  const hasChanges = newQuantity !== currentQuantity;

  // Initialize state when modal opens
  useEffect(() => {
    if (open) {
      setStep(0);
      setLocations(
        currentLocations.map((loc) => ({
          store_type: loc.store_type || "Medical",
          zip_code: loc.zip_code || "",
        }))
      );
      setProrationData(null);
      setError(null);
    }
  }, [open, currentLocations]);

  const fetchProrationData = async () => {
    setFetchingProration(true);
    setError(null);
    try {
      if (!subscriptionId) {
        throw new Error("No subscription ID available");
      }

      const response = await fetch(`${config.API_BASE_V2_URL}/subscriptions/location-proration`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          store_id: storeId,
          subscription_id: subscriptionId,
          new_quantity: newQuantity
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || result.message || "Failed to calculate proration");
      }

      const data = result.data;
      const isIncrease = data.is_increase;
      const prorationAmount = parseFloat(data.proration_amount);

      const formattedData = {
        is_upgrade: isIncrease,
        current_quantity: data.old_quantity,
        new_quantity: data.new_quantity,
        quantity_change: data.new_quantity - data.old_quantity,
        plan_amount: parseFloat(data.plan_unit_price).toFixed(2),
        days_remaining: data.remaining_days,
        proration_amount: Math.abs(prorationAmount).toFixed(2),
        due_today: isIncrease ? Math.abs(prorationAmount).toFixed(2) : "0.00",
        credit_applied: !isIncrease ? Math.abs(prorationAmount).toFixed(2) : "0.00",
        next_billing_amount: data.new_recurring_amount.toFixed(2),
        unused_credit: parseFloat(data.unused_credit || 0).toFixed(2),
        is_full_cycle_reset: true
      };

      setProrationData(formattedData);
      setStep(1); // Move to review step on success
    } catch (err) {
      console.error("Error fetching proration data:", err);
      setError(err.message);
    } finally {
      setFetchingProration(false);
    }
  };

  const handleNext = () => {
    if (newQuantity === currentQuantity) {
      // No quantity change, just save immediately (e.g. zip update)
      // Bypass review step
      handleConfirmSave();
    } else {
      fetchProrationData();
    }
  };

  const handleBack = () => {
    setStep(0);
    setError(null);
  };

  const handleDecrease = () => {
    if (locations.length > 1) {
      setLocations(locations.slice(0, -1));
    }
  };

  const handleIncrease = () => {
    if (locations.length < 10) {
      setLocations([...locations, { store_type: "Medical", zip_code: "" }]);
    }
  };

  const handleLocationChange = (index, field, value) => {
    const updated = [...locations];
    updated[index][field] = value;
    setLocations(updated);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    try {
      const locationsPayload = locations.map(loc => ({
        zip: loc.zip_code,
        type: loc.store_type
      }));

      const response = await fetch(`${config.API_BASE_V2_URL}/subscriptions/update-locations`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify({
          store_id: storeId,
          subscription_id: subscriptionId,
          new_quantity: locations.length,
          locations: locationsPayload
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || result.message || "Failed to update locations");
      }

      await onSave(locations, prorationData);

    } catch (error) {
      console.error("❌ Error saving locations:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Validate inputs
  const isValid = locations.every(
    (loc) => loc.store_type.trim() !== "" && loc.zip_code.trim() !== ""
  );

  return (
    <Dialog
      open={open}
      onClose={loading || fetchingProration ? null : onClose}
      maxWidth={step === 1 ? "md" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            {step === 1 && (
              <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {step === 0 ? "Manage Locations" : "Review Changes"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {step === 0
                  ? `Adjust locations on your ${currentPlan?.plan_name || "Basic"} Plan`
                  : "Review billing impact before confirming"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={loading || fetchingProration} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {step === 0 ? (
          // STEP 0: EDIT LOCATIONS
          <Box sx={{ p: 3 }}>
            {/* Current Plan Summary */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#F8FAFC', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" fontWeight={600} color="text.secondary">Current Plan</Typography>
                <Typography variant="subtitle2" fontWeight={700}>{currentPlan?.plan_name || "Basic"}</Typography>
              </Box>
              <Chip label={`$${currentPlan?.plan_amount || 0}/loc/mo`} size="small" sx={{ bgcolor: 'white', fontWeight: 600, border: '1px solid #E2E8F0' }} />
            </Paper>

            {/* Counter */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" fontWeight={700}>Total Locations</Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <IconButton
                  onClick={handleDecrease}
                  disabled={locations.length <= 1}
                  sx={{ border: '1px solid #E2E8F0', borderRadius: '8px' }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" fontWeight={700} sx={{ width: 30, textAlign: 'center' }}>{locations.length}</Typography>
                <IconButton
                  onClick={handleIncrease}
                  disabled={locations.length >= 10}
                  sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: 'primary.50', color: 'primary.main', '&:hover': { bgcolor: 'primary.100' } }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Location List */}
            <Box sx={{
              maxHeight: '320px',
              overflowY: 'auto',
              pr: 1,
              ml: -1, pl: 1, // Negate visual padding
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '3px' }
            }}>
              {locations.map((loc, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px', bgcolor: 'white' }}>
                  <Box display="flex" justifyContent="space-between" mb={1.5}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">LOCATION #{index + 1}</Typography>
                    {index >= currentQuantity && <Chip label="NEW" size="small" color="success" sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Store Type</InputLabel>
                        <Select
                          value={loc.store_type}
                          label="Store Type"
                          onChange={(e) => handleLocationChange(index, "store_type", e.target.value)}
                        >
                          <MenuItem value="Medical">Medical</MenuItem>
                          <MenuItem value="Recreational">Recreational</MenuItem>
                          <MenuItem value="Medical and Recreational">Medical and Recreational</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Zip Code"
                        placeholder="12345"
                        value={loc.zip_code}
                        onChange={(e) => {
                          const val = e.target.value.trim().toUpperCase();
                          if (/^[0-9-]*$/.test(val)) handleLocationChange(index, "zip_code", val);
                        }}
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          </Box>
        ) : (
          // STEP 1: REVIEW CHANGES
          <Box sx={{ display: 'flex', minHeight: '400px' }}>
            {/* Left Col: Summary Changes */}
            <Box sx={{ flex: 1.2, p: 3, borderRight: '1px solid #F1F5F9' }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>CHANGE SUMMARY</Typography>

              <Box mt={2} mb={4} display="flex" gap={2} alignItems="center">
                <Paper elevation={0} sx={{ flex: 1, p: 2, bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px dashed #CBD5E1' }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">CURRENT</Typography>
                  <Typography variant="h6" fontWeight={700}>{currentQuantity} Locations</Typography>
                </Paper>
                {prorationData?.is_upgrade ? <TrendingUpIcon color="primary" /> : <TrendingDownIcon color="warning" />}
                <Paper elevation={0} sx={{ flex: 1, p: 2, bgcolor: prorationData?.is_upgrade ? 'primary.50' : 'warning.50', borderRadius: '12px', border: '1px solid', borderColor: prorationData?.is_upgrade ? 'primary.100' : 'warning.100' }}>
                  <Typography variant="caption" fontWeight={700} color={prorationData?.is_upgrade ? 'primary.main' : 'warning.main'}>NEW TOTAL</Typography>
                  <Typography variant="h6" fontWeight={700}>{newQuantity} Locations</Typography>
                </Paper>
              </Box>

              <Alert severity="info" sx={{ mb: 3, borderRadius: '8px', '& .MuiAlert-message': { fontSize: '0.9rem' } }}>
                {prorationData?.is_upgrade
                  ? `Your new billing cycle starts TODAY. You will be credited $${prorationData?.unused_credit} for unused time on current plan.`
                  : `Your new billing cycle starts TODAY. A refund of $${prorationData?.credit_applied} will be applied for the difference.`
                }
              </Alert>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>New Location Details:</Typography>
                {locations.map((loc, i) => (
                  <Box key={i} display="flex" justifyContent="space-between" py={1} borderBottom="1px solid #F1F5F9">
                    <Typography variant="body2">{loc.store_type}</Typography>
                    <Typography variant="body2" color="text.secondary">{loc.zip_code}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Right Col: Financials */}
            <Box sx={{ flex: 0.8, p: 3, bgcolor: '#F8FAFC' }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>BILLING BREAKDOWN</Typography>

              <Paper elevation={0} sx={{ mt: 2, p: 3, borderRadius: '16px', bgcolor: 'white', border: '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">New Monthly Total</Typography>
                  <Typography variant="body2" fontWeight={600}>${prorationData?.next_billing_amount}/mo</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">Unused Credit</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">-${prorationData?.unused_credit}</Typography>
                </Box>

                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {prorationData?.is_upgrade ? "Net Due Today" : "Refund Amount"}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color={prorationData?.is_upgrade ? "primary.main" : "success.main"}>
                    ${prorationData?.is_upgrade ? prorationData?.due_today : prorationData?.credit_applied}
                  </Typography>
                </Box>
              </Paper>

              <Box mt={4}>
                <Box display="flex" gap={1} mb={1}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="caption" fontWeight={600}>Secure 256-bit SSL Encrypted</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  By confirming, you agree to start a new billing cycle effective immediately. Next automatic renewal: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #F1F5F9' }}>
        {error && <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>{error}</Typography>}

        {step === 0 ? (
          <>
            <Button onClick={onClose} sx={{ color: 'text.secondary', fontWeight: 600 }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isValid || fetchingProration}
              sx={{
                borderRadius: '10px', px: 4, fontWeight: 700,
                bgcolor: 'primary.main', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
              }}
            >
              {fetchingProration || loading ? <CircularProgress size={20} color="inherit" /> : (hasChanges ? "Review Changes" : "Save Changes")}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleBack} sx={{ color: 'text.secondary', fontWeight: 600 }}>Edit</Button>
            <Button
              variant="contained"
              onClick={handleConfirmSave}
              disabled={loading}
              sx={{
                borderRadius: '10px', px: 4, fontWeight: 700,
                bgcolor: prorationData?.is_upgrade ? 'primary.main' : 'success.main',
                '&:hover': { bgcolor: prorationData?.is_upgrade ? 'primary.dark' : 'success.dark' }
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : (prorationData?.is_upgrade ? "Confirm & Pay" : "Confirm Refund")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManageLocationsModal;